import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Download,
  Monitor,
  MonitorSmartphone,
  MousePointerClick,
  Share2,
  Smartphone,
  Wifi,
  MoreVertical,
} from "lucide-react";
import config from "../config";
import { cn } from "../utils/utils";
import useBrandTranslation from "../hooks/useBrandTranslation";

const PLATFORM = {
  IOS: "ios",
  ANDROID: "android",
  DESKTOP: "desktop",
};

const BROWSER = {
  CHROME: "chrome",
  EDGE: "edge",
  SAFARI: "safari",
  FIREFOX: "firefox",
  OPERA: "opera",
  OTHER: "other",
};

function detectPlatformAndBrowser() {
  if (typeof window === "undefined") {
    return { platform: PLATFORM.DESKTOP, browser: BROWSER.OTHER };
  }

  const ua = window.navigator.userAgent.toLowerCase();
  const isIOS = /iphone|ipad|ipod/.test(ua);
  const isAndroid = /android/.test(ua);
  const platform = isIOS
    ? PLATFORM.IOS
    : isAndroid
    ? PLATFORM.ANDROID
    : PLATFORM.DESKTOP;

  let browser = BROWSER.OTHER;

  if (ua.includes("edg/")) {
    browser = BROWSER.EDGE;
  } else if (ua.includes("opr") || ua.includes("opera")) {
    browser = BROWSER.OPERA;
  } else if (ua.includes("fxios") || ua.includes("firefox")) {
    browser = BROWSER.FIREFOX;
  } else if (ua.includes("crios") || ua.includes("chrome")) {
    browser = BROWSER.CHROME;
  } else if (ua.includes("safari")) {
    browser = BROWSER.SAFARI;
  }

  if (platform === PLATFORM.IOS) {
    browser = BROWSER.SAFARI;
  }

  return { platform, browser };
}

function getStepsByPlatform(t) {
  return {
    [PLATFORM.IOS]: [
      t("app.install.platformSteps.ios.1", "Abrí esta página en Safari."),
      t("app.install.platformSteps.ios.2", "Tocá el botón Compartir (icono cuadrado con flecha)."),
      t("app.install.platformSteps.ios.3", "Desplazate y elegí “Agregar a pantalla de inicio”."),
      t("app.install.platformSteps.ios.4", "Confirmá el nombre y tocá “Agregar”."),
    ],
    [PLATFORM.ANDROID]: [
      t("app.install.platformSteps.android.1", "Abrí esta página en Chrome."),
      t("app.install.platformSteps.android.2", "Tocá el menú ⋮ en la esquina superior derecha."),
      t("app.install.platformSteps.android.3", "Elegí “Agregar a pantalla principal”."),
      t("app.install.platformSteps.android.4", "Confirmá la acción para crear el acceso directo."),
    ],
    [PLATFORM.DESKTOP]: [
      t("app.install.platformSteps.desktop.1", "En Chrome, hacé clic en el icono de instalación (monitor con flecha) a la derecha de la barra de direcciones."),
      t("app.install.platformSteps.desktop.2", "Confirmá la instalación para agregar la app al escritorio y menú inicio."),
      t("app.install.platformSteps.desktop.3", "En Edge, usá el menú ⋯ > “Aplicaciones” > “Instalar este sitio como una aplicación”."),
    ],
  };
}

function getBrowserGuides(t) {
  return {
    "chrome-desktop": {
      icon: Monitor,
      title: t("app.install.guides.chromeDesktop.title", "Chrome en computadora"),
      location: t("app.install.guides.chromeDesktop.location", "Ícono de instalación a la derecha de la barra de direcciones."),
      steps: [
        t("app.install.guides.chromeDesktop.step1", "Hacé clic en el icono Instalar app (monitor con flecha o símbolo +)."),
        t("app.install.guides.chromeDesktop.step2", "Cuando aparezca el modal “Instalar app”, confirmá con Instalar."),
      ],
      note: t("app.install.guides.chromeDesktop.note", "Si no ves el icono, abrí el menú ⋮ y buscá “Instalar”."),
    },
    "edge-desktop": {
      icon: Monitor,
      title: t("app.install.guides.edgeDesktop.title", "Microsoft Edge"),
      location: t("app.install.guides.edgeDesktop.location", "Menú ⋯ en la esquina superior derecha."),
      steps: [
        t("app.install.guides.edgeDesktop.step1", "Hacé clic en ⋯ y elegí Aplicaciones."),
        t("app.install.guides.edgeDesktop.step2", "Seleccioná “Instalar este sitio como una aplicación”."),
        t("app.install.guides.edgeDesktop.step3", "Confirmá con Instalar."),
      ],
    },
    "chrome-android": {
      icon: MoreVertical,
      title: t("app.install.guides.chromeAndroid.title", "Chrome en Android"),
      location: t("app.install.guides.chromeAndroid.location", "Menú ⋮ ubicado arriba a la derecha."),
      steps: [
        t("app.install.guides.chromeAndroid.step1", "Tocá el menú ⋮ del navegador."),
        t("app.install.guides.chromeAndroid.step2", "Elegí “Instalar app” o “Agregar a pantalla principal”."),
        t("app.install.guides.chromeAndroid.step3", "Confirmá con Agregar."),
      ],
    },
    "safari-ios": {
      icon: Share2,
      title: t("app.install.guides.safariIos.title", "Safari en iPhone / iPad"),
      location: t("app.install.guides.safariIos.location", "Botón Compartir (cuadrado con flecha) en la barra inferior."),
      steps: [
        t("app.install.guides.safariIos.step1", "Tocá el ícono Compartir."),
        t("app.install.guides.safariIos.step2", "Desplazate y elegí “Agregar a pantalla de inicio”."),
        t("app.install.guides.safariIos.step3", "Confirmá con Agregar."),
      ],
    },
    "safari-desktop": {
      icon: Share2,
      title: t("app.install.guides.safariDesktop.title", "Safari en macOS"),
      location: t("app.install.guides.safariDesktop.location", "Menú “Archivo” en la barra superior."),
      steps: [
        t("app.install.guides.safariDesktop.step1", "En Safari, abrí el menú Archivo."),
        t("app.install.guides.safariDesktop.step2", "Seleccioná “Añadir a Dock”."),
        t("app.install.guides.safariDesktop.step3", "Confirmá y el acceso directo aparecerá en tu Dock."),
      ],
      note: t("app.install.guides.safariDesktop.note", "Esta opción está disponible en macOS Ventura o superior."),
    },
    "firefox-android": {
      icon: MoreVertical,
      title: t("app.install.guides.firefoxAndroid.title", "Firefox en Android"),
      location: t("app.install.guides.firefoxAndroid.location", "Menú ⋮ en la parte superior derecha."),
      steps: [
        t("app.install.guides.firefoxAndroid.step1", "Abrí el menú ⋮."),
        t("app.install.guides.firefoxAndroid.step2", "Elegí “Agregar a pantalla principal”."),
        t("app.install.guides.firefoxAndroid.step3", "Confirmá con Agregar."),
      ],
    },
    "opera-android": {
      icon: MoreVertical,
      title: t("app.install.guides.operaAndroid.title", "Opera / Samsung Internet"),
      location: t("app.install.guides.operaAndroid.location", "Menú del navegador en la parte inferior o superior."),
      steps: [
        t("app.install.guides.operaAndroid.step1", "Abrí el menú del navegador."),
        t("app.install.guides.operaAndroid.step2", "Seleccioná “Agregar a pantalla principal” o “Instalar app”."),
        t("app.install.guides.operaAndroid.step3", "Confirmá la acción."),
      ],
    },
    "other-android": {
      icon: Smartphone,
      title: t("app.install.guides.otherAndroid.title", "Otro navegador en Android"),
      location: t("app.install.guides.otherAndroid.location", "Menú del navegador."),
      steps: [
        t("app.install.guides.otherAndroid.step1", "Abrí el menú del navegador."),
        t("app.install.guides.otherAndroid.step2", "Buscá la opción “Agregar a pantalla principal” o “Instalar app”."),
        t("app.install.guides.otherAndroid.step3", "Confirmá los pasos que te solicite."),
      ],
    },
    "other-desktop": {
      icon: Monitor,
      title: t("app.install.guides.otherDesktop.title", "Otro navegador en computadora"),
      location: t("app.install.guides.otherDesktop.location", "Menú principal del navegador."),
      steps: [
        t("app.install.guides.otherDesktop.step1", "Buscá en el menú palabras como Instalar, Aplicación o Agregar a escritorio."),
        t("app.install.guides.otherDesktop.step2", "Seguí los pasos del navegador para confirmar la instalación."),
      ],
      note: t("app.install.guides.otherDesktop.note", "Algunos navegadores requieren usar Chrome o Edge para instalar la PWA."),
    },
    default: {
      icon: MousePointerClick,
      title: t("app.install.guides.default.title", "Instalación rápida"),
      location: t("app.install.guides.default.location", "Buscá en tu navegador la opción Instalar app o Agregar a pantalla de inicio."),
      steps: [
        t("app.install.guides.default.step1", "Abrí el menú principal del navegador."),
        t("app.install.guides.default.step2", "Seleccioná la opción de instalar o agregar a pantalla de inicio."),
        t("app.install.guides.default.step3", "Confirmá cuando el navegador te lo indique."),
      ],
    },
  };
}


export default function Install() {
  const { t } = useBrandTranslation();
  const [installPrompt, setInstallPrompt] = useState(null);
  const [installStatus, setInstallStatus] = useState({
    isStandalone: false,
    isInstallable: false,
    hasPromptEvent: false,
    platform: PLATFORM.DESKTOP,
  });
  const [browserInfo, setBrowserInfo] = useState(() =>
    detectPlatformAndBrowser()
  );
  const stepsSectionRef = useRef(null);

  useEffect(() => {
    const info = detectPlatformAndBrowser();
    const checkStandalone =
      window.matchMedia("(display-mode: standalone)").matches ||
      window.navigator.standalone === true;

    setBrowserInfo(info);
    setInstallStatus((prev) => ({
      ...prev,
      platform: info.platform,
      isStandalone: checkStandalone,
    }));

    const handleBeforeInstallPrompt = (event) => {
      event.preventDefault();
      setInstallPrompt(event);
      setInstallStatus((prev) => ({
        ...prev,
        hasPromptEvent: true,
        isInstallable: true,
      }));
    };

    window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);

    return () => {
      window.removeEventListener(
        "beforeinstallprompt",
        handleBeforeInstallPrompt
      );
    };
  }, []);

  useEffect(() => {
    const handler = () => {
      const checkStandalone =
        window.matchMedia("(display-mode: standalone)").matches ||
        window.navigator.standalone === true;
      setInstallStatus((prev) => ({ ...prev, isStandalone: checkStandalone }));
    };

    window.addEventListener("appinstalled", handler);

    return () => window.removeEventListener("appinstalled", handler);
  }, []);

  const currentSteps = useMemo(() => {
    const steps = getStepsByPlatform(t);
    return steps[installStatus.platform] ?? steps[PLATFORM.DESKTOP];
  }, [installStatus.platform, t]);

  const browserGuides = useMemo(() => getBrowserGuides(t), [t]);

  const handleInstallClick = async () => {
    if (!installPrompt) return;
    installPrompt.prompt();
    const { outcome } = await installPrompt.userChoice;
    setInstallStatus((prev) => ({
      ...prev,
      isInstallable: outcome === "accepted",
    }));
    setInstallPrompt(null);
  };

  const handleInstallCta = () => {
    if (installPrompt) {
      handleInstallClick();
      return;
    }

    if (installStatus.isStandalone) {
      return;
    }

    stepsSectionRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };

  const theme = useMemo(() => {
    return {
      primaryColor: config.theme?.colors?.primaryColor ?? "#2563EB",
      secondaryColor: config.theme?.colors?.secondaryColor ?? "#0EA5E9",
    };
  }, []);

  const browserGuide = useMemo(() => {
    const comboKey = `${browserInfo.browser}-${browserInfo.platform}`;

    if (browserGuides[comboKey]) {
      return browserGuides[comboKey];
    }

    if (browserInfo.platform === PLATFORM.ANDROID) {
      return browserGuides["other-android"];
    }

    if (browserInfo.platform === PLATFORM.IOS) {
      return browserGuides["safari-ios"];
    }

    if (browserInfo.platform === PLATFORM.DESKTOP) {
      return browserGuides["other-desktop"];
    }

    return browserGuides.default;
  }, [browserInfo, browserGuides]);

  return (
    <div className="min-h-screen bg-gray-50">
      <section
        className="relative overflow-hidden text-white"
        style={{
          backgroundImage: `linear-gradient(135deg, ${theme.primaryColor}, ${theme.secondaryColor})`,
        }}
      >
        <div className="absolute inset-0 bg-white/10" />
        <div className="relative mx-auto flex max-w-5xl flex-col gap-10 px-6 py-20 text-center sm:px-10">
          <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-white/15">
            <Download className="h-8 w-8" />
          </div>
          <div className="space-y-4">
            <h1 className="text-3xl font-bold sm:text-5xl">
              {t("app.install.heroTitle", "Instala {{brand}} como aplicación", { brand: config.brand })}
            </h1>
            <p className="text-lg text-white/90 sm:text-xl">
              {t("app.install.heroSubtitle", "Llevá la plataforma a tu escritorio o pantalla de inicio para un acceso rápido, notificaciones inmediatas y uso sin distracciones.")}
            </p>
          </div>
          <div className="flex flex-col items-center justify-center gap-3">
            <InstallStatusBadge status={installStatus} />
            <div className="flex flex-col gap-2 sm:flex-row">
              <button
                onClick={handleInstallCta}
                className="inline-flex items-center justify-center gap-2 rounded-full bg-white px-6 py-3 text-lg font-semibold text-gray-900 shadow transition hover:bg-gray-100 focus:outline-none focus-visible:ring focus-visible:ring-white/70"
              >
                <Download className="h-5 w-5" />
                {t("app.install.installApp", "Instalar aplicación")}
              </button>
              {installStatus.hasPromptEvent && (
                <button
                  onClick={handleInstallClick}
                  className={cn(
                    "inline-flex items-center justify-center gap-2 rounded-full px-6 py-3 text-lg font-semibold transition focus:outline-none focus-visible:ring",
                    installPrompt
                      ? "bg-white/20 text-white hover:bg-white/30 focus-visible:ring-white/60"
                      : "bg-white/10 text-white/70 cursor-not-allowed"
                  )}
                  disabled={!installPrompt}
                >
                  <Download className="h-5 w-5" />
                  {installPrompt
                    ? t("app.install.installFromBrowser", "Instalar desde el navegador")
                    : t("app.install.waitingAvailability", "Esperando disponibilidad…")}
                </button>
              )}
            </div>
            {!installStatus.hasPromptEvent && !installStatus.isStandalone && (
              <p className="text-sm text-white/80">
                {t("app.install.noAutoButton", "Si tu navegador no ofrece el botón automáticamente, seguí los pasos más abajo.")}
              </p>
            )}
          </div>
          <BrowserInstallGuide guide={browserGuide} />
        </div>
    </section>

    <section
      ref={stepsSectionRef}
      className="mx-auto w-full max-w-5xl px-6 py-16 sm:px-10"
    >
      <header className="mb-8 text-center">
        <h2 className="text-2xl font-semibold text-gray-800 sm:text-3xl">
          {t("app.install.stepsTitle", "Instalación rápida según tu dispositivo")}
        </h2>
        <p className="mt-3 text-gray-600">
          {t("app.install.stepsSubtitle", "Seguí estos pasos y en segundos vas a tener {{brand}} como una app más.", { brand: config.brand })}
        </p>
      </header>

      <ol className="mx-auto max-w-3xl space-y-6 text-left text-gray-700">
        {currentSteps.map((step, index) => (
          <li
            key={step}
            className="flex items-start gap-4 rounded-2xl bg-white p-5 shadow-sm ring-1 ring-gray-100"
          >
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-gray-900 text-lg font-semibold text-white">
              {index + 1}
            </span>
            <span className="leading-relaxed">{step}</span>
          </li>
        ))}
      </ol>

    </section>



    <section className="mx-auto max-w-5xl px-6 pb-20 pt-8 sm:px-10">
      <div className="rounded-3xl bg-gray-900 p-8 text-white shadow-xl sm:p-12">
        <h3 className="text-2xl font-semibold">
          {t("app.install.alreadyInstalled", "¿Ya instalaste {{brand}}?", { brand: config.brand })}
        </h3>
        <p className="mt-3 text-white/80">
          {t("app.install.alreadyInstalledHint", "Podés anclarla a tu barra de tareas o mover el ícono al lugar que te quede más cómodo. Si necesitás ayuda adicional escribinos a soporte.")}
        </p>
        <div className="mt-6 flex flex-col gap-3 text-white/70 sm:flex-row sm:items-center sm:justify-between">
          <span>
            {t("app.install.alsoWeb", "También podés volver al inicio para seguir usando la versión web.")}
          </span>
          <button
            onClick={() => window.location.assign("/")}
            className="inline-flex items-center justify-center rounded-full bg-white px-5 py-2 text-sm font-semibold text-gray-900 shadow transition hover:bg-gray-100 focus:outline-none focus-visible:ring focus-visible:ring-white/60"
          >
            {t("app.install.goHome", "Ir al inicio")}
          </button>
        </div>
      </div>
    </section>
  </div>
  );
}

function InstallStatusBadge({ status }) {
  const { t } = useBrandTranslation();
  if (status.isStandalone) {
    return (
      <span className="rounded-full bg-white/25 px-5 py-2 text-sm uppercase tracking-wide text-white shadow">
        {t("app.install.badgeInstalled", "Ya está instalada en este dispositivo")}
      </span>
    );
  }

  if (status.hasPromptEvent) {
    return (
      <span className="rounded-full bg-white/25 px-5 py-2 text-sm uppercase tracking-wide text-white shadow">
        {t("app.install.badgeAvailable", "Instalación disponible")}
      </span>
    );
  }

  return null;
}

function BrowserInstallGuide({ guide }) {
  if (!guide) {
    return null;
  }

  const GuideIcon = guide.icon ?? MousePointerClick;

  return (
    <div className="mx-auto w-full max-w-3xl text-left">
      <div className="rounded-3xl bg-white/15 p-6 text-left text-white shadow-lg backdrop-blur">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:gap-6">
          <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-white/20">
            <GuideIcon className="h-6 w-6" />
          </div>
          <div className="space-y-4">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-white/70">
                {guide.location}
              </p>
              <h3 className="mt-2 text-xl font-semibold">{guide.title}</h3>
            </div>
            <ul className="space-y-2 text-sm text-white/90 sm:text-base">
              {guide.steps.map((step, index) => (
                <li
                  key={`${index}-${step}`}
                  className="flex items-start gap-3 text-left"
                >
                  <MousePointerClick className="mt-1 h-4 w-4 shrink-0 text-white/70" />
                  <span>{step}</span>
                </li>
              ))}
            </ul>
            {guide.note && (
              <p className="text-xs text-white/70">{guide.note}</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

